<script setup lang="ts">
import { useMinerModelFamilies } from '@/composables/core/miner-model-families'
import { useTenants } from '@/composables/core/tenants'
import { useRepairRequestSubmissions } from '@/composables/rc/repair-request-submissions'
import { useServices } from '@/composables/rc/services'
import type { ITenantFilters } from '@/types/core/filters'
import type { IServiceFilters } from '@/types/rc/filters'
import type { ICustomerRepairRequestSubmissionForm } from '@/types/rc/forms'
import { Icon } from '@iconify/vue'
import type { OptionType } from 'element-plus/es/components/select-v2/src/select.types.mjs'
import { computed, reactive, ref } from 'vue'

const open = defineModel<boolean>()

const formRef = ref()

// Form data
const form = reactive<ICustomerRepairRequestSubmissionForm>({
  tenant_id: '',
  scope_of_work: [],
  expected_units: 1,
  miner_models: [],
  notes: '',
})

// Query filters
const tenantFilters = reactive<ITenantFilters>({
  page: 1,
  per_page: 100,
  fragment: undefined,
})
const serviceFilters = reactive<IServiceFilters>({})

// Composables
const { createCustomerRepairRequestSubmissionMutation } = useRepairRequestSubmissions()
const { minerModelFamilyOptions, isPending: minerModelsLoading } = useMinerModelFamilies()
const { getTenantsQuery } = useTenants()
const { getServicesQuery } = useServices()
const { mutateAsync: createCustomerRepairRequestSubmissionAsync, isPending: isCreatePending } =
  createCustomerRepairRequestSubmissionMutation

// Tenants query
const { data: tenantsData, isPending: tenantsLoading } = getTenantsQuery(tenantFilters)

const tenantOptions = computed<OptionType[]>(() => {
  if (!tenantsData.value?.data) return []
  return tenantsData.value.data.map((tenant) => ({
    label: tenant.company_name,
    value: tenant.tenant_id.toString(),
  }))
})

const isTenantSelected = computed(() => {
  return form.tenant_id !== undefined && form.tenant_id !== ''
})

// Services query - depends on tenant selection
const { data: servicesData, isPending: servicesLoading } = getServicesQuery(serviceFilters, isTenantSelected)

const serviceOptions = computed<OptionType[]>(() => {
  if (!servicesData.value) return []
  return servicesData.value.map((service) => ({
    label: service.description,
    value: service.id,
    raw: service,
  }))
})

// Form validation rules
const rules = {
  tenant_id: [{ required: true, message: 'Please select a tenant', trigger: 'change' }],
  scope_of_work: [{ required: true, message: 'Please select at least one service', trigger: 'change' }],
  expected_units: [
    { required: true, message: 'Please enter expected units', trigger: 'blur' },
    { type: 'number' as const, min: 1, message: 'Expected units must be at least 1', trigger: 'blur' },
  ],
  miner_models: [{ required: true, message: 'Please select at least one miner model', trigger: 'change' }],
}

// Computed properties
const isFormDisabled = computed(() => !form.tenant_id)
const canSubmit = computed(() => {
  return form.tenant_id && form.scope_of_work.length > 0 && form.expected_units >= 1 && form.miner_models.length > 0
})

const handleTenantChange = (newTenantId: string) => {
  if (newTenantId) {
    serviceFilters.tenant_id = newTenantId
  } else {
    // Reset the scope of work
    form.scope_of_work = []
    serviceFilters.tenant_id = undefined
  }
}

// Methods
const resetForm = () => {
  form.tenant_id = ''
  form.scope_of_work = []
  form.expected_units = 1
  form.miner_models = []
  form.notes = ''
  formRef.value?.resetFields()
}

const handleSubmit = async () => {
  if (!formRef.value) return

  try {
    await formRef.value.validate()

    await createCustomerRepairRequestSubmissionAsync(form)

    // Reset form and close dialog on success
    open.value = false
  } catch {
    // Handled by mutation error handler
  }
}

const handleCancel = () => {
  open.value = false
}

const handleDialogClosed = () => {
  resetForm()
}
</script>

<template>
  <el-dialog
    v-model="open"
    title="Create Repair Request Submission"
    width="600"
    :show-close="false"
    @closed="handleDialogClosed"
  >
    <el-form
      ref="formRef"
      :model="form"
      :rules="rules"
      label-position="top"
      label-width="auto"
      :disabled="isCreatePending"
    >
      <!-- Tenant Selection -->
      <el-form-item label="Repair Center" prop="tenant_id" required>
        <el-select-v2
          v-model="form.tenant_id"
          :options="tenantOptions"
          placeholder="Select a repair center..."
          filterable
          clearable
          :loading="tenantsLoading"
          no-data-text="No repair centers found"
          value-key="value"
          class="w-full"
          @change="handleTenantChange"
        />
      </el-form-item>

      <!-- Scope of Work -->
      <el-form-item label="Requested Services" prop="scope_of_work" required>
        <el-select-v2
          v-model="form.scope_of_work"
          :options="serviceOptions"
          placeholder="Select services"
          multiple
          filterable
          clearable
          :loading="servicesLoading"
          :disabled="isFormDisabled"
          no-data-text="No services found for this tenant"
          value-key="value"
          class="w-full"
        >
          <template #default="{ item }">
            <div class="flex items-center gap-2">
              <Icon :icon="item.raw.icon" :height="16" />
              <span>{{ item.raw.description }}</span>
            </div>
            <span>{{ item.raw.description }} ({{ item.raw.price }})</span>
          </template>
        </el-select-v2>
      </el-form-item>

      <!-- Expected Units -->
      <el-form-item label="Equipment Quantity" prop="expected_units" required>
        <el-input-number
          v-model="form.expected_units"
          :min="1"
          :disabled="isFormDisabled"
          placeholder="Enter expected units"
          class="w-full"
        />
      </el-form-item>

      <!-- Miner Models -->
      <el-form-item label="Equipment Models" prop="miner_models" required>
        <el-select-v2
          v-model="form.miner_models"
          :options="minerModelFamilyOptions || []"
          placeholder="Select miner models"
          multiple
          filterable
          clearable
          :loading="minerModelsLoading"
          :disabled="isFormDisabled"
          no-data-text="No miner models found"
          value-key="value"
          class="w-full"
        />
      </el-form-item>

      <!-- Notes -->
      <el-form-item label="Notes" prop="notes">
        <el-input
          v-model="form.notes"
          type="textarea"
          :rows="4"
          :disabled="isFormDisabled"
          placeholder="Enter any additional notes"
          maxlength="500"
          show-word-limit
          class="w-full"
        />
      </el-form-item>
    </el-form>

    <template #footer>
      <div class="flex justify-end gap-3">
        <el-button :disabled="isCreatePending" @click="handleCancel"> Cancel </el-button>
        <el-button type="primary" :loading="isCreatePending" :disabled="!canSubmit" @click="handleSubmit">
          Create Submission
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<style scoped lang="scss">
// Add any custom styles if needed
</style>
